/**
 * FailuresTab (F-3) — 본 스펙 MONITORING_SPEC.md §5.
 *
 * 정상일 0행. 사고일에 8 테넌트 통합 실패 행.
 * 행 액션: → 파이프라인 (탭3 으로 이동), → fix_loop 재실행.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { fetchFailures } from "../api/monitoring";
import type { FailureRow, FixLoopStatus } from "../api/monitoring";
import { runSkill } from "../api/skills";

const STATUS_COLOR: Record<FixLoopStatus, "success" | "error" | "warning" | "info" | "default"> = {
  escalated: "error",
  failed: "error",
  running: "info",
  pending: "warning",
  success: "success",
};

interface Props {
  bsda: string;
  onJumpToPipeline: (tenantId: string, promptId: string) => void;
}

export function FailuresTab({ bsda, onJumpToPipeline }: Props) {
  const [rows, setRows] = useState<FailureRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState<Set<string>>(new Set());
  const [snack, setSnack] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const r = await fetchFailures(bsda);
      setRows(r.failures);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [bsda]);

  useEffect(() => {
    void reload();
    const t = setInterval(() => void reload(), 30_000);
    return () => clearInterval(t);
  }, [reload]);

  const onRerun = useCallback(
    async (row: FailureRow) => {
      const key = `${row.tenant_id}-${row.prompt_id}`;
      setRunning((prev) => new Set(prev).add(key));
      try {
        await runSkill(row.tenant_id, "fix_loop", {
          bsda,
          converterType: row.prompt_id,
        });
        setSnack(`fix_loop 재실행 트리거됨: ${row.tenant_name_korean} / ${row.prompt_id}`);
        setTimeout(() => void reload(), 2_000);
      } catch (e) {
        setSnack(`재실행 실패: ${e instanceof Error ? e.message : String(e)}`);
      } finally {
        setRunning((prev) => {
          const next = new Set(prev);
          next.delete(key);
          return next;
        });
      }
    },
    [bsda, reload]
  );

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>테넌트</TableCell>
              <TableCell>prompt_id</TableCell>
              <TableCell>status</TableCell>
              <TableCell align="right">fail JSON</TableCell>
              <TableCell align="right">fix 시도</TableCell>
              <TableCell>마지막 fix_pattern</TableCell>
              <TableCell align="center">액션</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={20} />
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="success.main" fontWeight={600}>
                    ✅ 이 날짜에 실패/진행 prompt 가 없습니다 ({bsda})
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => {
              const key = `${r.tenant_id}-${r.prompt_id}`;
              const isRerunning = running.has(key);
              return (
                <TableRow key={key} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip size="small" label={r.tenant_id} variant="outlined" />
                      <Typography variant="body2">{r.tenant_name_korean}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {r.prompt_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={r.status} color={STATUS_COLOR[r.status]} />
                  </TableCell>
                  <TableCell align="right">{r.fail_count}</TableCell>
                  <TableCell align="right">{r.fix_attempt_count}</TableCell>
                  <TableCell>
                    <Typography variant="caption" fontFamily="monospace">
                      {r.last_fix_pattern || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<OpenInNewIcon />}
                        onClick={() => onJumpToPipeline(r.tenant_id, r.prompt_id)}
                      >
                        파이프라인
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        startIcon={<PlayArrowIcon />}
                        disabled={isRerunning || r.status === "running"}
                        onClick={() => void onRerun(r)}
                      >
                        {isRerunning ? "트리거 중…" : "fix_loop 재실행"}
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        message={snack || ""}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
